import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/apiService';
import {
  defaultPodcastEpisodePayload,
  serializePodcastEpisodeDraft,
} from '../portal/podcastEpisodeEnvelope';
import type { DraftNotebookKind } from '../utils/draftNotebookKind';
import type { NewsCluster } from '../types/types';

// Define the PodcastData type
export type PodcastData = {
  id?: string;
  title: string;
  summary: string;
  content: string;
  createdAt: string;
  /** ISO string — content edits or API `updated_at` */
  updatedAt?: string;
  duration: number;
  language: string;
  visibility?: string;
  /** When visibility is `restricted`, profile ids allowed to open this podcast (same model as notebooks). */
  allowed_profiles?: string[];
  /** Portal notebooks linked to this podcast draft (hex ids). */
  linked_notebook_ids?: string[];
  /** Cluster ids from generation (when returned by API). */
  cluster_ids?: string[];
  /** Resolved cluster objects (Library editor); not always returned by list API. */
  clusters?: NewsCluster[];
  notebookId?: string;
  notebookName?: string;
  status?: string; // 'active', 'archived', 'trash'
};

// Define the podcast job status type
export type PodcastJobStatus = {
  job_id: string;
  status: "processing" | "completed" | "failed";
  created_at: string;
  completed_at?: string;
  error?: string;
  podcast?: {
    id: string;
    title: string;
    description: string;
    content: string;
    language: string;
    visibility: string;
    status: string;
    created_at: number;
    updated_at: number;
    linked_notebook_ids?: string[];
    cluster_ids?: string[];
  };
};

// Define the context value type
type PodcastsContextType = {
  podcasts: PodcastData[];
  isLoading: boolean;
  /** True once a fetch has succeeded; lets deep links tell "not loaded (yet)" from "missing". */
  hasLoaded: boolean;
  generationProgress: string | null;
  fetchPodcasts: () => Promise<boolean>;
  generatePodcast: (
    content: any[],
    duration: number,
    language: string,
    visibility: string,
    notebookId?: string,
    notebookName?: string,
    /** Library draft kind that triggered generation (sent to API for tailored prompting). */
    sourceDraftKind?: DraftNotebookKind
  ) => Promise<PodcastData | null>;
  updatePodcast: (id: string, updates: Partial<PodcastData>) => Promise<boolean>;
  addClustersToPodcast: (podcastId: string, clustersToAdd: NewsCluster[]) => Promise<boolean>;
  removeClustersFromPodcast: (podcastId: string, clusterRefs: string[]) => Promise<boolean>;
  changePodcastStatus: (id: string, status: string) => Promise<boolean>;
  deletePodcast: (id: string) => Promise<boolean>;
  createBlankPodcast: () => Promise<PodcastData | null>;
  cancelGeneration: () => void;
  error: string | null;
  setError: (error: string | null) => void;
};

// Create the context with default values
export const PodcastsContext = createContext<PodcastsContextType>({
  podcasts: [],
  isLoading: false,
  hasLoaded: false,
  generationProgress: null,
  fetchPodcasts: async () => false,
  generatePodcast: async () => null,
  updatePodcast: async () => false,
  addClustersToPodcast: async () => false,
  removeClustersFromPodcast: async () => false,
  changePodcastStatus: async () => false,
  deletePodcast: async () => false,
  createBlankPodcast: async () => null,
  cancelGeneration: () => {},
  error: null,
  setError: () => {}
});

// Define props for the PodcastsProvider
type PodcastsProviderProps = {
  children: ReactNode;
};

// Default polling interval in milliseconds
const DEFAULT_POLLING_INTERVAL = 3000;

const uniq = (ids: string[]): string[] => Array.from(new Set(ids.filter(Boolean)));
const toClusterRefId = (cluster: NewsCluster): string => (cluster.cluster_id || '').trim();

export const PodcastsProvider: React.FC<PodcastsProviderProps> = ({ children }) => {
  const [podcasts, setPodcasts] = useState<PodcastData[]>([]);
  const podcastsRef = useRef<PodcastData[]>([]);
  useEffect(() => {
    podcastsRef.current = podcasts;
  }, [podcasts]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [, setCurrentJobId] = useState<string | null>(null);
  const [generationProgress, setGenerationProgress] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);

  // Check for saved job ID on mount
  useEffect(() => {
    const savedJobId = localStorage.getItem('podcastGenerationJobId');
    if (savedJobId) {
      setCurrentJobId(savedJobId);
      setIsLoading(true);
      setGenerationProgress('Reconnecting to draft generation…');
      pollPodcastGeneration(savedJobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only reconnect; pollPodcastGeneration defined below
  }, []);

  // Cancel any active polling when component unmounts
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Fetch podcasts from backend API (stable ref — LayoutLibrary only loads when tab + empty list;
  // a new function every render retriggered the effect forever while podcasts.length stayed 0.)
  const fetchPodcasts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get('/api/podcasts');
      console.log('Fetched podcasts:', response);

      if (response && Array.isArray(response)) {
        console.log('Number of podcasts from API:', response.length);

        const backendPodcasts = response.map((podcast: any) => {
          const createdAt = podcast.created_at
            ? new Date(podcast.created_at * 1000).toISOString()
            : new Date().toISOString();
          const updatedAt =
            podcast.updated_at != null && podcast.updated_at !== ''
              ? new Date(
                  typeof podcast.updated_at === 'number'
                    ? podcast.updated_at * 1000
                    : podcast.updated_at
                ).toISOString()
              : undefined;
          const rawLinked = (podcast as { linked_notebook_ids?: unknown }).linked_notebook_ids;
          const linkedIds =
            Array.isArray(rawLinked) && rawLinked.length
              ? rawLinked.map((x: unknown) => (typeof x === 'string' ? x : String(x))).filter(Boolean)
              : undefined;
          return {
            id: podcast.id,
            title: podcast.title || 'Untitled Podcast',
            summary: podcast.description || 'No summary available',
            content: podcast.content || podcast.description || 'No content available',
            createdAt,
            updatedAt: updatedAt || createdAt,
            duration: typeof podcast.duration === 'number' ? podcast.duration : 5,
            language: podcast.language || '',
            visibility: podcast.visibility || 'private',
            allowed_profiles: Array.isArray(podcast.allowed_profiles) ? podcast.allowed_profiles : [],
            linked_notebook_ids: linkedIds,
            cluster_ids: Array.isArray(podcast.cluster_ids)
              ? podcast.cluster_ids.map((x: unknown) => String(x)).filter(Boolean)
              : [],
            notebookId: podcast.owner_profile_id || '',
            status: podcast.status || 'active',
          };
        });

        setPodcasts(backendPodcasts);
      }
      setHasLoaded(true);
      return true;
    } catch (error: any) {
      console.error('Error fetching podcasts:', error);
      setError(error.message || 'Failed to load podcasts');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Start podcast generation process
  const startPodcastGeneration = async (
    clusterContent: any[],
    duration: number,
    language: string,
    visibility: string,
    notebookId?: string,
    notebookName?: string,
    sourceDraftKind?: DraftNotebookKind
  ): Promise<string> => {
    // Format the data for the backend
    const formattedClusters = clusterContent.map(cluster => {
      // Process contrasting views - map complex objects to strings if needed
      let contrastingViews: string[] = [];
      if (Array.isArray(cluster.contrasting_views)) {
        contrastingViews = cluster.contrasting_views.map((view: any) => 
          typeof view === 'object' && view.point_of_view_description 
            ? view.point_of_view_description 
            : String(view)
        );
      }
      
      return {
        title: cluster.title || '',
        detailed_summary: cluster.detailed_summary || '',
        article_text: cluster.article_text || '',
        contrasting_views: contrastingViews
      };
    });
    
    // Define request data interface
    interface PodcastGenerationRequest {
      cluster_content: {
        title: string;
        detailed_summary: string;
        article_text: string;
        contrasting_views: string[];
      }[];
      duration: number;
      language: string;
      visibility: string;
      notebook_id?: string;
      notebook_name?: string;
      source_draft_kind?: DraftNotebookKind;
    }
    
    // Build the request object with all required fields
    const requestData: PodcastGenerationRequest = {
      cluster_content: formattedClusters,
      duration: duration || 5,
      language: language || 'English',
      visibility: visibility || 'private',
    };
    
    // Add optional fields if they exist
    if (notebookId) requestData.notebook_id = notebookId;
    if (notebookName) requestData.notebook_name = notebookName;
    if (sourceDraftKind) requestData.source_draft_kind = sourceDraftKind;
    
    console.log('Request payload size:', JSON.stringify(requestData).length, 'bytes');
    
    // Call our backend API to start the generation process
    const response = await api.post('/api/podcasts/generate', requestData);
    
    console.log('Generation job started:', response);
    
    // Store the job ID and return it
    return response.job_id;
  };

  // Check the status of a podcast generation job
  const checkPodcastJobStatus = async (jobId: string): Promise<PodcastJobStatus> => {
    const response = await api.get(`/api/podcasts/jobs/${jobId}`);
    return response;
  };

  // Calculate elapsed time in seconds
  const calculateElapsedTime = (startTime: Date): string => {
    const now = new Date();
    const seconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
    return `${seconds}s`;
  };

  // Poll for podcast generation status
  const pollPodcastGeneration = async (
    jobId: string, 
    intervalMs: number = DEFAULT_POLLING_INTERVAL
  ): Promise<PodcastData | null> => {
    // Clear any existing interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    // Store job ID for persistence across page reloads
    localStorage.setItem('podcastGenerationJobId', jobId);
    setCurrentJobId(jobId);

    // Use a local variable to track the interval
    let intervalId: number | null = null;

    return new Promise<PodcastData | null>((resolve, reject) => {
      const checkStatus = async () => {
        try {
          const jobStatus = await checkPodcastJobStatus(jobId);
          
          // Update progress for UI
          if (jobStatus.created_at) {
            const startTime = new Date(jobStatus.created_at);
            const elapsed = calculateElapsedTime(startTime);
            setGenerationProgress(`Generating draft… (${elapsed} elapsed)`);
          } else {
            setGenerationProgress('Generating draft…');
          }
          
          if (jobStatus.status === "completed" && jobStatus.podcast) {
            // Clear the interval immediately to stop further polling
            if (intervalId !== null) {
              window.clearInterval(intervalId);
              setPollingInterval(null);
            }
            
            // Job completed successfully
            clearSavedJobData();
            
            // Retrieve stored metadata from localStorage
            const storedDuration = localStorage.getItem(`podcast_duration_${jobId}`);
            const storedLanguage = localStorage.getItem(`podcast_language_${jobId}`);
            const storedVisibility = localStorage.getItem(`podcast_visibility_${jobId}`);
            const storedNotebookId = localStorage.getItem(`podcast_notebook_id_${jobId}`);
            const storedNotebookName = localStorage.getItem(`podcast_notebook_name_${jobId}`);
            
            // Convert the backend podcast format to our PodcastData format
            const createdAt = jobStatus.podcast.created_at
              ? new Date(jobStatus.podcast.created_at * 1000).toISOString()
              : new Date().toISOString();
            const updatedAt =
              jobStatus.podcast.updated_at != null
                ? new Date(jobStatus.podcast.updated_at * 1000).toISOString()
                : createdAt;
            const jobPodcastExtras = jobStatus.podcast as unknown as {
              allowed_profiles?: unknown;
              linked_notebook_ids?: unknown;
              cluster_ids?: unknown;
            };
            const rawAllowed = jobPodcastExtras.allowed_profiles;
            const allowedFromJob =
              Array.isArray(rawAllowed) && rawAllowed.every((x): x is string => typeof x === 'string')
                ? rawAllowed
                : [];
            const rawJobLinked = jobPodcastExtras.linked_notebook_ids;
            const linkedFromJob =
              Array.isArray(rawJobLinked) && rawJobLinked.length
                ? rawJobLinked.map((x: unknown) => (typeof x === 'string' ? x : String(x))).filter(Boolean)
                : undefined;
            const rawClusterIds = jobPodcastExtras.cluster_ids;
            const clusterIdsFromJob =
              Array.isArray(rawClusterIds) && rawClusterIds.length
                ? rawClusterIds.map((x: unknown) => (typeof x === 'string' ? x : String(x))).filter(Boolean)
                : undefined;
            const podcastData: PodcastData = {
              id: jobStatus.podcast.id,
              title: jobStatus.podcast.title || "Generated Podcast",
              summary: jobStatus.podcast.description || "",
              content: jobStatus.podcast.content || "",
              createdAt,
              updatedAt,
              duration: storedDuration ? parseInt(storedDuration, 10) : 5,
              language: storedLanguage || jobStatus.podcast.language || "English",
              visibility: storedVisibility || jobStatus.podcast.visibility || "private",
              allowed_profiles: allowedFromJob,
              linked_notebook_ids: linkedFromJob,
              cluster_ids: clusterIdsFromJob,
              notebookId: storedNotebookId || undefined,
              notebookName: storedNotebookName || undefined,
              status: jobStatus.podcast.status || 'active'
            };
            
            // Clean up stored metadata
            localStorage.removeItem(`podcast_duration_${jobId}`);
            localStorage.removeItem(`podcast_language_${jobId}`);
            localStorage.removeItem(`podcast_visibility_${jobId}`);
            localStorage.removeItem(`podcast_notebook_id_${jobId}`);
            localStorage.removeItem(`podcast_notebook_name_${jobId}`);
            
            // Update the podcasts state
            setPodcasts(prevPodcasts => [podcastData, ...prevPodcasts]);
            
            // Set loading to false
            setIsLoading(false);
            
            resolve(podcastData);
            return;
          } else if (jobStatus.status === "failed") {
            // Clear the interval immediately
            if (intervalId !== null) {
              window.clearInterval(intervalId);
              setPollingInterval(null);
            }
            
            // Job failed
            clearSavedJobData();
            
            // Clean up stored metadata
            localStorage.removeItem(`podcast_duration_${jobId}`);
            localStorage.removeItem(`podcast_language_${jobId}`);
            localStorage.removeItem(`podcast_visibility_${jobId}`);
            localStorage.removeItem(`podcast_notebook_id_${jobId}`);
            localStorage.removeItem(`podcast_notebook_name_${jobId}`);
            
            // Set loading to false
            setIsLoading(false);
            
            setError(jobStatus.error || 'Draft generation failed');
            reject(new Error(jobStatus.error || 'Draft generation failed'));
            return;
          }
          // Status is still "processing", continue polling
        } catch (error: any) {
          // Clear the interval on error
          if (intervalId !== null) {
            window.clearInterval(intervalId);
            setPollingInterval(null);
          }
          
          console.error("Error checking podcast job status:", error);
          clearSavedJobData();
          
          // Clean up stored metadata
          localStorage.removeItem(`podcast_duration_${jobId}`);
          localStorage.removeItem(`podcast_language_${jobId}`);
          localStorage.removeItem(`podcast_visibility_${jobId}`);
          localStorage.removeItem(`podcast_notebook_id_${jobId}`);
          localStorage.removeItem(`podcast_notebook_name_${jobId}`);
          
          // Set loading to false
          setIsLoading(false);
          
          setError(error.message || 'Failed to check draft generation status');
          reject(error);
        }
      };
      
      // Do initial check
      checkStatus();
      
      // Set up interval for polling
      intervalId = window.setInterval(checkStatus, intervalMs);
      setPollingInterval(intervalId);
    });
  };

  // Clear saved job data
  const clearSavedJobData = () => {
    localStorage.removeItem('podcastGenerationJobId');
    setCurrentJobId(null);
    setGenerationProgress(null);
    
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Cancel current generation if any
  const cancelGeneration = () => {
    clearSavedJobData();
    setIsLoading(false);
    setGenerationProgress(null);
    // Note: Backend doesn't support cancellation yet, this just stops the polling
  };

  // Generate podcast content using backend API
  const generatePodcast = async (
    clusterContent: any[],
    duration: number,
    language: string,
    visibility: string,
    notebookId?: string,
    notebookName?: string,
    sourceDraftKind?: DraftNotebookKind
  ): Promise<PodcastData | null> => {
    setIsLoading(true);
    setError(null);
    setGenerationProgress('Starting draft generation…');
    
    try {
      // Validate required fields
      if (!clusterContent || clusterContent.length === 0) {
        throw new Error('No content provided for draft generation');
      }
      
      // Start the generation process
      const jobId = await startPodcastGeneration(
        clusterContent,
        duration,
        language,
        visibility,
        notebookId,
        notebookName,
        sourceDraftKind
      );
      
      // Store additional metadata in localStorage for retrieval during polling
      if (notebookId) {
        localStorage.setItem(`podcast_notebook_id_${jobId}`, notebookId);
      }
      if (notebookName) {
        localStorage.setItem(`podcast_notebook_name_${jobId}`, notebookName);
      }
      
      // Store duration, language and visibility for retrieval during polling
      localStorage.setItem(`podcast_duration_${jobId}`, duration.toString());
      localStorage.setItem(`podcast_language_${jobId}`, language);
      localStorage.setItem(`podcast_visibility_${jobId}`, visibility);
      
      // Start polling for results
      return await pollPodcastGeneration(jobId);
    } catch (error: any) {
      console.error('Error generating draft:', error);
      setError(error.message || 'Failed to generate draft');
      return null;
    } finally {
      // Note: we don't set isLoading to false here because it will be done
      // when the polling completes or fails
    }
  };

  const addClustersToPodcast = async (podcastId: string, clustersToAdd: NewsCluster[]) => {
    setError(null);
    try {
      const podcast = podcastsRef.current.find((p) => p.id === podcastId);
      if (!podcast) {
        throw new Error('Podcast not found');
      }
      const currentClusters = podcast.clusters || [];
      const existingTitles = new Set(currentClusters.map((c) => c.title));
      const processedClusters = clustersToAdd
        .filter((c) => !existingTitles.has(c.title))
        .map((cluster) => ({
          ...cluster,
          articles: cluster.articles || [],
          suggested_titles: cluster.suggested_titles || [],
          social_media: cluster.social_media || [],
          contrasting_views: cluster.contrasting_views || [],
          wiki_concepts: cluster.wiki_concepts || [],
          timeline: cluster.timeline || [],
        }));
      if (processedClusters.length === 0) {
        return true;
      }
      const incomingIds = processedClusters.map(toClusterRefId);
      if (incomingIds.some((id) => !id)) {
        throw new Error('Some clusters are missing cluster_id and cannot be attached to this podcast.');
      }
      const updatedClusters = [...currentClusters, ...processedClusters];
      const updatedClusterIds = uniq([...(podcast.cluster_ids || []), ...incomingIds]);

      await api.put(`/api/podcasts/${podcastId}`, {
        cluster_ids: updatedClusterIds,
        updated_at: Math.floor(Date.now() / 1000),
      });

      const touchedAt = new Date().toISOString();
      setPodcasts((prev) =>
        prev.map((p) =>
          p.id === podcastId
            ? { ...p, clusters: updatedClusters, cluster_ids: updatedClusterIds, updatedAt: touchedAt }
            : p
        )
      );
      return true;
    } catch (error: any) {
      console.error('Error adding clusters to podcast:', error);
      setError(error?.message || 'Failed to add clusters to podcast');
      return false;
    }
  };

  const removeClustersFromPodcast = async (podcastId: string, clusterRefs: string[]) => {
    setError(null);
    try {
      const podcast = podcastsRef.current.find((p) => p.id === podcastId);
      if (!podcast) {
        throw new Error('Podcast not found');
      }
      const refs = new Set(clusterRefs.filter(Boolean));
      const currentClusters = podcast.clusters || [];
      const updatedClusters = currentClusters.filter(
        (c) => !refs.has((c.cluster_id || '').trim()) && !refs.has(c.title)
      );
      let updatedClusterIds = uniq(updatedClusters.map(toClusterRefId).filter(Boolean));
      if (currentClusters.length === 0 && (podcast.cluster_ids || []).length > 0) {
        updatedClusterIds = (podcast.cluster_ids || []).filter((id) => !refs.has(id));
      }

      await api.put(`/api/podcasts/${podcastId}`, {
        cluster_ids: updatedClusterIds,
        updated_at: Math.floor(Date.now() / 1000),
      });

      const touchedAt = new Date().toISOString();
      setPodcasts((prev) =>
        prev.map((p) =>
          p.id === podcastId
            ? { ...p, clusters: updatedClusters, cluster_ids: updatedClusterIds, updatedAt: touchedAt }
            : p
        )
      );
      return true;
    } catch (error: any) {
      console.error('Error removing clusters from podcast:', error);
      setError(error?.message || 'Failed to remove clusters from podcast');
      return false;
    }
  };

  // Update existing podcast
  const updatePodcast = async (id: string, updates: Partial<PodcastData>) => {
    if (!id) return false;

    try {
      const backendUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) backendUpdates.title = updates.title;
      if (updates.summary !== undefined) backendUpdates.description = updates.summary;
      if (updates.content !== undefined) backendUpdates.content = updates.content;
      if (updates.visibility !== undefined) backendUpdates.visibility = updates.visibility;
      if (updates.allowed_profiles !== undefined) backendUpdates.allowed_profiles = updates.allowed_profiles;
      if (updates.linked_notebook_ids !== undefined) backendUpdates.linked_notebook_ids = updates.linked_notebook_ids;
      if (updates.duration !== undefined) backendUpdates.duration = updates.duration;
      if (updates.language !== undefined) backendUpdates.language = updates.language;
      if (updates.cluster_ids !== undefined) backendUpdates.cluster_ids = updates.cluster_ids;

      if (Object.keys(backendUpdates).length > 0) {
        backendUpdates.updated_at = Math.floor(Date.now() / 1000);

        await api.put(`/api/podcasts/${id}`, backendUpdates);

        const touchedAt = new Date().toISOString();
        setPodcasts((prevPodcasts) =>
          prevPodcasts.map((p) => {
            if (p.id !== id) return p;
            const next: PodcastData = { ...p, ...updates, updatedAt: updates.updatedAt ?? touchedAt };
            if (updates.clusters !== undefined) {
              next.clusters = updates.clusters;
            }
            return next;
          })
        );

        return true;
      }

      if (updates.clusters !== undefined && Object.keys(backendUpdates).length === 0) {
        const touchedAt = new Date().toISOString();
        setPodcasts((prevPodcasts) =>
          prevPodcasts.map((p) => (p.id === id ? { ...p, clusters: updates.clusters, updatedAt: touchedAt } : p))
        );
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('Error updating podcast:', error);
      setError(error.message || 'Failed to update podcast');
      return false;
    }
  };

  // Change podcast status (active/archived/trash)
  const changePodcastStatus = async (id: string, status: string) => {
    if (!id) return false;

    try {
      await api.put(`/api/podcasts/${id}`, {
        status: status
      });
      
      // Update the local state
      setPodcasts(prevPodcasts => 
        prevPodcasts.map(p => 
          p.id === id ? { ...p, status } : p
        )
      );
      
      return true;
    } catch (error: any) {
      console.error(`Error changing podcast status to ${status}:`, error);
      setError(error.message || `Failed to change podcast status to ${status}`);
      return false;
    }
  };

  const createBlankPodcast = async (): Promise<PodcastData | null> => {
    setError(null);
    try {
      const res = (await api.post('/api/podcasts', {
        title: 'New podcast draft',
        description: '',
        content: '',
        language: 'English',
        visibility: 'private',
      })) as { id?: string };
      const id = res?.id;
      if (!id) {
        setError('Podcast create did not return an id');
        return null;
      }
      const payload = defaultPodcastEpisodePayload(id);
      payload.title = 'New podcast draft';
      const content = serializePodcastEpisodeDraft(payload);
      await api.put(`/api/podcasts/${id}`, {
        title: payload.title,
        description: payload.show,
        content,
        updated_at: Math.floor(Date.now() / 1000),
      });
      await fetchPodcasts();
      const nowIso = new Date().toISOString();
      const created: PodcastData = {
        id,
        title: payload.title,
        summary: payload.show,
        content,
        createdAt: nowIso,
        updatedAt: nowIso,
        duration: 0,
        language: 'English',
        visibility: 'private',
        allowed_profiles: [],
        linked_notebook_ids: [],
        status: 'active',
      };
      return created;
    } catch (err: any) {
      console.error('createBlankPodcast', err);
      setError(err?.message || 'Failed to create podcast');
      return null;
    }
  };

  // Delete podcast permanently
  const deletePodcast = async (id: string) => {
    if (!id) return false;

    try {
      await api.delete(`/api/podcasts/${id}`);
      
      // Remove the podcast from local state
      setPodcasts(prevPodcasts => 
        prevPodcasts.filter(p => p.id !== id)
      );
      
      return true;
    } catch (error: any) {
      console.error('Error deleting podcast:', error);
      setError(error.message || 'Failed to delete podcast');
      return false;
    }
  };

  return (
    <PodcastsContext.Provider 
      value={{ 
        podcasts, 
        isLoading, 
        hasLoaded,
        generationProgress,
        fetchPodcasts, 
        generatePodcast, 
        updatePodcast,
        addClustersToPodcast,
        removeClustersFromPodcast,
        changePodcastStatus, 
        deletePodcast,
        createBlankPodcast,
        cancelGeneration,
        error,
        setError
      }}
    >
      {children}
    </PodcastsContext.Provider>
  );
};

export default PodcastsProvider; 